// ---------------------------------------------------------------------------
// Device settings, in a modal.
//
// A modal rather than a page: everything in here is a per-device reading
// preference that somebody changes in the middle of doing something else, and a
// page would mean leaving a lobby to turn animations off.
//
// **Everything here is stored on this device and belongs to nobody's account.**
// A phone and a laptop signed into the same account get their own answers,
// which is right for preferences about eyes and screens.
//
// The theme row is deliberately the same control that lives in the account
// menu, reading and writing the same store — two ways to the one setting, like
// the room's privacy chip and its Visibility segment. A setting that exists in
// two places and disagrees with itself is a bug; two controls over one value are
// not.
//
// Motion is the reason this dialog exists. See `settingsStore.ts`: the OS
// preference seeds the value once on a new device and is never read again, so
// this control is the only thing that decides afterwards. The copy says so,
// because "reduce motion" next to a machine that also has a reduce-motion
// setting is exactly where somebody would expect the two to keep arguing.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import { prefersReducedMotion, useSettingsStore, type Theme } from '../../store/settingsStore.js';
import { swallowNextClick } from '../../lib/swallowNextClick.js';
import {
  CloseIcon,
  Icon,
  MoonIcon,
  SunIcon,
  SystemThemeIcon,
  type IconData,
} from '../primitives/Icon.js';

const THEMES: readonly { value: Theme; label: string; icon: IconData }[] = [
  { value: 'dark', label: 'Dark', icon: MoonIcon },
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'system', label: 'System', icon: SystemThemeIcon },
];

export interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

function Row({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: ReactElement;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-line py-4 last:border-b-0">
      <div className="flex flex-col gap-1">
        <span className="text-[15px] font-semibold text-ink">{title}</span>
        <span className="text-[13px] leading-relaxed text-ink-muted">{hint}</span>
      </div>
      {children}
    </div>
  );
}

export function SettingsDialog({ open, onClose }: SettingsDialogProps): ReactElement | null {
  const theme = useSettingsStore((s) => s.theme);
  const setTheme = useSettingsStore((s) => s.setTheme);
  const setReducedMotion = useSettingsStore((s) => s.setReducedMotion);
  // Subscribed so the toggle re-renders on change, but READ through
  // `prefersReducedMotion`, which resolves the never-answered case and writes
  // the seed down. A raw `null` here would draw an unchecked switch on a machine
  // that had asked for less motion and was getting it.
  useSettingsStore((s) => s.reducedMotion);
  const motionOff = prefersReducedMotion();

  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      data-testid="settings-scrim"
      className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6"
      style={{ background: 'var(--c-scrim)' }}
      // A press on the scrim closes; a press inside must not. `pointerdown`
      // rather than click, matching the account menu.
      //
      // **And the click that press is about to make is eaten.** Closing here
      // unmounts the scrim before the finger comes up, so the browser hit-tests
      // the following `click` against whatever is at that point NOW — and this
      // dialog is opened from the header, over a whole live screen. The scrim's
      // top-right corner sits directly over the avatar that opened it, so a tap
      // to get out of Settings lands on the avatar and the account menu opens
      // on the way out. Everything else the scrim covers is fair game too: the
      // home link, the chat button, a lobby row's three dots. See
      // `lib/swallowNextClick.ts`; it is the same report Isaac made about the
      // host row menu, in the one other place the shape occurs.
      //
      // Moving the dismissal to `click` would also fix it and is not done here,
      // for consistency rather than necessity: this dialog holds no drag, so it
      // could afford the change, but two scrims in the app dismissing on
      // different events is how the next one gets written wrong. The theme
      // radios and the motion switch inside the panel are unaffected either way
      // — the guard above returns before any of this for a press on the panel.
      //
      // Guarded on the panel, which is the whole of "the press landed on the
      // backdrop": this element covers the screen and the panel is the only
      // thing in it. A menu with no backdrop must NOT do this — see the note in
      // `swallowNextClick` — but there is a real darkened area here, so a press
      // on it is unambiguously "get me out" and nothing else.
      onPointerDown={(event) => {
        if (panelRef.current?.contains(event.target as Node) === true) return;
        swallowNextClick();
        onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-y-auto rounded-t-card border border-line bg-surface p-5 sm:rounded-card"
      >
        <div className="flex shrink-0 items-center gap-3 pb-1">
          <h2 className="text-[19px] font-bold tracking-[-0.01em]">Settings</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border border-line text-ink-muted transition-colors duration-150 hover:border-line-strong hover:text-ink"
          >
            <Icon icon={CloseIcon} size={17} />
          </button>
        </div>

        <Row
          title="Theme"
          hint="System follows whatever this device is set to, and keeps following it."
        >
          <div role="radiogroup" aria-label="Theme" className="grid grid-cols-3 gap-1.5">
            {THEMES.map((option) => {
              const on = theme === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setTheme(option.value)}
                  className={`flex min-h-11 items-center justify-center gap-2 rounded-[10px] border text-[13px] font-semibold transition-colors duration-150 ${
                    on
                      ? 'border-blue-500 bg-blue-500 text-ink-on-fill'
                      : 'border-line text-ink-muted hover:border-line-strong hover:text-ink'
                  }`}
                >
                  <Icon icon={option.icon} size={15} />
                  {option.label}
                </button>
              );
            })}
          </div>
        </Row>

        <Row
          title="Reduce motion"
          // The second sentence is the whole reason this control is worth
          // having, and it is stated plainly because the alternative is somebody
          // toggling it, seeing nothing change, and concluding it is broken —
          // which is what happened while the stylesheet was reading the machine
          // behind this setting's back.
          hint="Tiles arrive without turning over and nothing pulses. Set from this device the first time you visited; it is yours now, and changing the device's own setting will not move it."
        >
          <button
            type="button"
            role="switch"
            aria-checked={motionOff}
            onClick={() => setReducedMotion(!motionOff)}
            className={`flex min-h-11 items-center justify-between gap-3 rounded-[10px] border px-3.5 text-[14px] font-semibold transition-colors duration-150 ${
              motionOff
                ? 'border-blue-500 bg-blue-500 text-ink-on-fill'
                : 'border-line text-ink-muted hover:border-line-strong hover:text-ink'
            }`}
          >
            <span>{motionOff ? 'On' : 'Off'}</span>
            <span
              aria-hidden="true"
              className={`flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors duration-150 ${
                motionOff ? 'bg-ink-on-fill/35' : 'bg-line'
              }`}
            >
              <span
                className={`h-5 w-5 rounded-full transition-transform duration-150 ${
                  motionOff ? 'translate-x-5 bg-ink-on-fill' : 'bg-ink-muted'
                }`}
              />
            </span>
          </button>
        </Row>
      </div>
    </div>
  );
}
